// Data + presentation for the Agents catalog. Used by AgentsPage.

import React from "react";
import { Link } from "react-router-dom";
import { SectionLabel, LiveDot } from "./ui";
import WhatsAppDemo from "./WhatsAppDemo";

export const AGENTS = [
  {
    id: "whatsapp", code: "WA.01", name: "WhatsApp Commerce Agent", tag: "Channel · WhatsApp · Voice", isWhatsApp: true,
    headline: "Your customer's favourite channel becomes your fastest sales rep.",
    problem: "Your buyers already live on WhatsApp. Today an order means a phone call, a screenshot, a price check, a credit lookup, then a manual order entry. By the time it hits your ERP, half the day is gone.",
    role: ["Greets, identifies and serves every WhatsApp customer end-to-end", "Speaks Bangla, English, Hindi, Bahasa, Urdu, Arabic — and switches mid-chat", "Pulls live pricing, credit limit, past orders from your ERP", "Takes orders, voice notes, photos, edits — places the PO autonomously", "Updates the customer when the truck leaves and confirms delivery + payment"],
    kpi: [{ k: "Order capture time", v: "12 min → 90s" }, { k: "Orders / month", v: "+62%" }, { k: "Service cost / chat", v: "−88%" }],
    tools: ["WhatsApp Business API · Meta", "Voice transcription · 22 languages", "SAP · Oracle · NetSuite · custom ERP", "EkkBaz Pay / payment gateways"],
    terminal: [],
  },
  {
    id: "ar-collections", code: "AR.01", name: "AR Collections Agent", tag: "Finance · Receivables",
    headline: "Recovers receivables before they become bad debt.",
    problem: "Tier-2 retailers go dark. Collections calls cost more than they recover. Aging buckets quietly bleed working capital.",
    role: ["Reads ERP aging in real time, prioritizes by recovery probability × invoice size", "Multi-channel outreach: WhatsApp, voice, email — personalized in customer's language", "Negotiates payment plans inside guardrails you set", "Escalates disputes to humans with full context attached", "Posts expected receipts to your cash-flow forecast"],
    kpi: [{ k: "DSO reduction", v: "−18 days" }, { k: "Recovery rate", v: "94%" }, { k: "Labor reclaimed", v: "92%" }],
    tools: ["SAP · Oracle · NetSuite", "WhatsApp Business API", "Twilio voice", "DocuSign", "Slack escalation"],
    terminal: [
      { role: "trigger", text: "ERP: 312 invoices crossed 30d aging" },
      { role: "think",   text: "Ranking by recovery probability × ticket size" },
      { role: "tool",    text: "tool.call(whatsapp.send) × 47 — bilingual templates" },
      { role: "result",  text: "21 retailers committed · $284,200 expected this week" },
      { role: "learn",   text: "Fri 11am outreach +18% response — saved to policy" },
    ],
  },
  {
    id: "supplier-onboarding", code: "PR.01", name: "Supplier Onboarding Agent", tag: "Procurement",
    headline: "Vets, verifies and contracts new suppliers without a buyer touching it.",
    problem: "Onboarding a single supplier takes 21 days, costs $400+, and 4 in 10 fail KYB on first pass.",
    role: ["Intake parsing — pulls supplier docs into structured data", "Multi-jurisdiction KYB · UBO · sanctions · adverse-media checks", "Risk-tier categorization with explainable scores", "Auto-generated MSAs and NDAs in your templates", "Pushes supplier records to ERP, payment portal and procurement queue"],
    kpi: [{ k: "Onboarding cycle", v: "21d → 38h" }, { k: "KYB pass rate", v: "97%" }, { k: "Cost / supplier", v: "$420 → $28" }],
    tools: ["Dun & Bradstreet", "World-Check", "DocuSign · PandaDoc", "SAP Ariba · Coupa", "Stripe · banking APIs"],
    terminal: [
      { role: "trigger", text: "Supplier intake form submitted" },
      { role: "tool",    text: "tool.call(kyb.verify) — registry + sanctions + UBO" },
      { role: "think",   text: "Risk tier: T2 · matching to standard MSA template" },
      { role: "tool",    text: "tool.call(docusign.send) — pre-filled MSA + NDA" },
      { role: "result",  text: "Supplier production-ready in 38h · full audit trail" },
    ],
  },
  {
    id: "cx-concierge", code: "CX.01", name: "CX Concierge Agent", tag: "Customer Experience",
    headline: "Resolves, retains and upsells across every channel — in 22 languages.",
    problem: "Tickets pile up, agents copy-paste from playbooks, first-contact resolution stalls at ~44%, and CSAT slips.",
    role: ["Omnichannel — chat, email, voice, WhatsApp, social, in-app", "Pulls full customer 360 before responding (CRM, order, ticket history)", "Resolves on its own when confident; routes to humans with full context when not", "Detects upsell / churn signals and acts within rules you set", "Quality-scores every interaction and feeds back into playbooks"],
    kpi: [{ k: "First-contact resolution", v: "89%" }, { k: "CSAT", v: "4.7 / 5" }, { k: "Cost / contact", v: "−71%" }],
    tools: ["Salesforce · HubSpot · Zendesk", "Intercom · Front", "Twilio · Vonage", "WhatsApp · Meta", "Custom CRMs via API"],
    terminal: [
      { role: "trigger", text: "Inbound: 'My room AC isn't cooling — checking out tomorrow.'" },
      { role: "think",   text: "Guest = Platinum · 3 prior stays · low complaint rate" },
      { role: "tool",    text: "tool.call(pms.lookup) — room 1402 has 2 past AC tickets" },
      { role: "act",     text: "Dispatched engineering · offered upgrade · 480pt credit" },
      { role: "result",  text: "Guest accepted · CSAT 5/5 · maintenance ticket filed" },
    ],
  },
  {
    id: "demand-forecast", code: "SC.01", name: "Demand Forecast Agent", tag: "Supply Chain · Planning",
    headline: "Forecasts SKU-level demand and triggers replenishment automatically.",
    problem: "Forecast accuracy stalls at ~70%, stockouts and excess inventory fight each other, and planners can't keep up with festivals, weather and competitor moves.",
    role: ["Blends POS, weather, festival calendar, competitor pricing, social signals", "Forecasts SKU × outlet × week with confidence intervals", "Triggers POs against your supplier matrix with backup vendors ranked", "Detects demand shocks early — Eid, monsoon, viral trends", "Retrains itself nightly; promotes new models only when they beat champion"],
    kpi: [{ k: "Forecast accuracy", v: "94%" }, { k: "Stockouts / mo", v: "184 → 12" }, { k: "Excess inventory", v: "−78%" }],
    tools: ["SAP IBP · Oracle SCM", "Power BI · Tableau", "POS connectors", "Weather APIs", "Supplier portals"],
    terminal: [
      { role: "trigger", text: "Nightly: 12,400 SKUs × 480 outlets" },
      { role: "think",   text: "Eid in 11 days · cooking oil demand +340%" },
      { role: "tool",    text: "tool.call(erp.po_create) — 14 expedited POs" },
      { role: "result",  text: "Saved est. $412K in lost sales · zero spoilage" },
      { role: "learn",   text: "Festival uplift model retrained · accuracy +3.2pp" },
    ],
  },
  {
    id: "salesforce", code: "SL.01", name: "Salesforce Automation Agent", tag: "Sales · Field Force",
    headline: "Runs your outside sales force without the overhead.",
    problem: "Reps spend 65% of their day on admin. Coverage is uneven. Trade promos leak. Visit reports come back days late, if at all.",
    role: ["Plans daily beats — optimal routes, priority outlets, talking points", "Captures visit outcomes via voice or photo and structures them automatically", "Auto-files trade promo claims and reconciles against POS", "Surfaces under-performing reps and coaches in real time", "Predicts churn at outlet level and pre-empts it"],
    kpi: [{ k: "Rep productive hours", v: "+38%" }, { k: "Promo leakage", v: "−62%" }, { k: "Outlet coverage", v: "94%" }],
    tools: ["Salesforce SFA", "EkkBaz Field SFA", "Google Maps Routing", "Power Apps · Mendix", "Voice + image AI"],
    terminal: [
      { role: "trigger", text: "Morning planning · 47 reps · 1,840 outlets in scope" },
      { role: "act",     text: "Generated optimal beats — 11% fewer km, 7% more visits" },
      { role: "tool",    text: "tool.call(voice.transcribe) — visit notes structured live" },
      { role: "result",  text: "Promo claims auto-filed · $48K reconciled today" },
    ],
  },
  {
    id: "inventory", code: "WH.01", name: "Inventory Intelligence Agent", tag: "Warehouse · Inventory",
    headline: "Real-time stock truth across every location.",
    problem: "Stock counts drift, slow-movers tie up cash, fast-movers go OOS, and your team plays whack-a-mole between locations.",
    role: ["Live inventory ledger across DCs, hubs, outlets and 3PLs", "Auto-rebalancing recommendations — and execution if you let it", "Slow-mover detection with markdown / liquidation playbook", "Cycle-count optimization — counts only what matters", "Loss & shrinkage anomaly detection"],
    kpi: [{ k: "Stock accuracy", v: "99.4%" }, { k: "Working capital", v: "−$3.1M" }, { k: "Shrinkage caught", v: "+340%" }],
    tools: ["SAP EWM · Manhattan", "RFID scanners", "Custom WMS APIs", "BI dashboards"],
    terminal: [
      { role: "trigger", text: "DC-Dhaka shows stockout risk on 14 SKUs in 48h" },
      { role: "think",   text: "DC-Chittagong has surplus of 9 of those · rebalance" },
      { role: "tool",    text: "tool.call(transport.book) — 1 truck, 18h ETA" },
      { role: "result",  text: "Stockouts avoided · $94K margin protected" },
    ],
  },
  {
    id: "warehouse", code: "WH.02", name: "Warehouse Operations Agent", tag: "Warehouse · Fulfillment",
    headline: "Pick, pack and ship — orchestrated by an agent that learns from every shift.",
    problem: "Lines of pickers walking miles, mis-picks burning margin, dock chaos at peak, and no one can predict when the wave will land.",
    role: ["Wave planning that respects truck departure windows + perishability", "Pick-path optimization in real time, including vision-validated picks", "Dock scheduling that doesn't break when trucks arrive late", "Daily exception report — every minute lost, why", "Predicts the next shift's volume and staffs accordingly"],
    kpi: [{ k: "Lines / hour", v: "+44%" }, { k: "Mis-pick rate", v: "0.12%" }, { k: "Dock idle time", v: "−51%" }],
    tools: ["Manhattan WMS", "SAP EWM", "Zebra scanners", "Computer vision", "Robotics integrations"],
    terminal: [
      { role: "trigger", text: "Inbound wave: 4,800 lines · cutoff in 6h" },
      { role: "act",     text: "Generated wave plan — 11 zones, 38 pickers, 5 waves" },
      { role: "tool",    text: "tool.call(vision.verify) — flagged 3 mis-picks live" },
      { role: "result",  text: "Wave shipped 22 min early · zero rejections" },
    ],
  },
  {
    id: "delivery", code: "LG.01", name: "Delivery & Logistics Agent", tag: "Last Mile · Logistics",
    headline: "Adapts to the real world — traffic, weather, no-show drivers and all.",
    problem: "Static routes break the moment something changes. Failed deliveries compound. Customers get a 4-hour window and a shrug.",
    role: ["Dynamic routing that re-plans every 90 seconds", "Driver behavior coaching with explainable nudges", "Failed-delivery prediction with proactive customer outreach", "ETA precision down to 12-minute windows", "POD verification via photo + signature + geofence"],
    kpi: [{ k: "On-time delivery", v: "97.8%" }, { k: "Cost / drop", v: "−24%" }, { k: "Failed deliveries", v: "−68%" }],
    tools: ["Onfleet · Bringg · Routific", "Google Maps · Mapbox", "WhatsApp customer comms", "Photo POD"],
    terminal: [
      { role: "trigger", text: "Driver D-94 stuck in traffic · 7 stops at risk" },
      { role: "think",   text: "Re-routing 4 stops to D-22 · notifying 3 customers" },
      { role: "tool",    text: "tool.call(whatsapp.send) — new 25-min windows" },
      { role: "result",  text: "All deliveries on-time · CSAT 4.8/5" },
    ],
  },
  {
    id: "distributor", code: "CH.01", name: "Distributor Management Agent", tag: "Channel · Distribution",
    headline: "Keeps your distributor network in sync, in stock and in compliance.",
    problem: "Channel partners run their own playbooks, secondary sales are guesses, and trade promos are a black box.",
    role: ["Auto-collects secondary sales from distributor systems", "Detects channel stuffing, parallel imports and price leaks", "Generates partner scorecards and improvement plans", "Recommends and executes credit-limit changes within policy", "Coaches under-performing partners with concrete next actions"],
    kpi: [{ k: "Secondary visibility", v: "100%" }, { k: "Price leaks caught", v: "+812%" }, { k: "Partner satisfaction", v: "4.6 / 5" }],
    tools: ["EkkBaz DMS", "SAP · Oracle", "WhatsApp Business", "POS sync"],
    terminal: [
      { role: "trigger", text: "Detected price below MRP at 23 outlets in Sylhet" },
      { role: "think",   text: "Traced to distributor D-417 · channel stuffing risk" },
      { role: "act",     text: "Froze next allocation · scheduled compliance call" },
      { role: "result",  text: "Leak closed in 36h · $112K margin recovered" },
    ],
  },
  {
    id: "financing", code: "FN.01", name: "Embedded Financing Agent", tag: "Finance · Credit",
    headline: "Approves credit at the speed of an order — for customers a bank would reject.",
    problem: "Small retailers can't buy what they could sell because credit decisions take days and they don't fit traditional risk models.",
    role: ["Builds alternative credit scores from transaction history, mobile data, satellite imagery", "Pre-approves credit lines at the order screen", "Adjusts limits dynamically based on behavior", "Detects early signals of distress", "Hands disputes to humans with full context"],
    kpi: [{ k: "Approval speed", v: "42 sec" }, { k: "Default rate", v: "1.8%" }, { k: "Credit penetration", v: "+340%" }],
    tools: ["EkkBaz Pay", "Local credit bureaus", "Mobile telco data", "Bank APIs", "Risk models · custom"],
    terminal: [
      { role: "trigger", text: "Order: $1,840 · retailer R-04829 · no prior credit" },
      { role: "think",   text: "Alt-score 712 · 14mo history · seasonal pattern OK" },
      { role: "act",     text: "Approved $2,500 line · 30-day terms" },
      { role: "result",  text: "Order shipped · retailer activated for repeat" },
    ],
  },
];

const RoleChipMini = ({ role }) => {
  const map = {
    trigger: { tag: "TRIG", chip: "bg-rose-400/10 text-rose-200" },
    think:   { tag: "THINK",  chip: "bg-cyan-300/10 text-cyan-200" },
    tool:    { tag: "TOOL",   chip: "bg-violet-400/10 text-violet-200" },
    act:     { tag: "ACT",    chip: "bg-blue-400/10 text-blue-200" },
    result:  { tag: "RESULT", chip: "bg-emerald-400/10 text-emerald-200" },
    learn:   { tag: "LEARN",  chip: "bg-amber-300/10 text-amber-200" },
  };
  const c = map[role];
  return <div className={`shrink-0 rounded px-1.5 py-[2px] font-mono text-[9px] tracking-widest ${c.chip}`}>{c.tag}</div>;
};

const roleColor = (r) => ({
  result: "text-emerald-200", learn: "text-amber-200", think: "text-cyan-200",
  tool: "text-violet-200", act: "text-blue-200", trigger: "text-rose-200",
}[r] || "text-white");

export const AgentCard = ({ a }) => (
  <section id={a.id} className="scroll-mt-24 relative py-16 border-t border-white/[0.06]">
    <div className="mx-auto max-w-7xl px-6 lg:px-10">
      <div className="grid items-start gap-10 lg:grid-cols-[1.05fr,1fr]">
        <div>
          <div className="flex items-center gap-3">
            <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-cyan-300/80">{a.code}</span>
            <span className="h-px w-12 bg-gradient-to-r from-cyan-300/40 to-transparent" />
            <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-white/40">{a.tag}</span>
          </div>
          <h2 className="mt-4 text-[34px] leading-[1.05] tracking-tight sm:text-[42px]"><span className="text-white">{a.name}</span></h2>
          <p className="mt-3 font-display italic text-[20px] text-white/80">{a.headline}</p>

          <div className="mt-6 rounded-xl border border-rose-300/15 bg-rose-300/[0.04] p-4">
            <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-rose-200/80">The pain</div>
            <p className="mt-1.5 text-[14px] text-white/75">{a.problem}</p>
          </div>

          <div className="mt-6">
            <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-cyan-300/80">What the agent actually does</div>
            <ul className="mt-3 space-y-2.5">
              {a.role.map((r,i) => (
                <li key={i} className="flex items-start gap-2.5 text-[14px] text-white/80">
                  <svg className="mt-[3px] h-3.5 w-3.5 flex-shrink-0 text-cyan-300" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6"><path d="M5 13l4 4L19 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
                  <span>{r}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="mt-6 flex flex-wrap items-center gap-2">
            <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-white/40">Integrates with</span>
            {a.tools.map(t => <span key={t} className="rounded border border-white/[0.08] bg-white/[0.03] px-2 py-1 font-mono text-[10.5px] text-white/55">{t}</span>)}
          </div>

          <div className="mt-7 flex flex-wrap gap-2">
            <a href="/#book" className="inline-flex items-center gap-2 rounded-full bg-white px-5 py-2.5 text-[13.5px] font-semibold text-[#051030] hover:-translate-y-0.5">
              Build this agent for us
              <svg className="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M5 12h14M13 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
            </a>
            <Link to="/#demo" className="inline-flex items-center gap-2 rounded-full border border-white/15 px-5 py-2.5 text-[13.5px] font-semibold text-white hover:bg-white/[0.05]">
              See it in action
            </Link>
          </div>
        </div>

        <div>
          {a.isWhatsApp ? (
            <>
              <WhatsAppDemo height={520} />
              <div className="mt-4 grid grid-cols-3 gap-px overflow-hidden rounded-xl border border-white/[0.06] bg-white/[0.06]">
                {a.kpi.map(k => (
                  <div key={k.k} className="bg-[#070d24] p-4">
                    <div className="font-mono text-[9.5px] uppercase tracking-[0.18em] text-white/40">{k.k}</div>
                    <div className="mt-1 text-[20px] font-semibold tracking-tight nums grad-text">{k.v}</div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <>
              <div className="rounded-2xl border border-white/[0.08] bg-gradient-to-b from-[#0b1535] to-[#070d24] overflow-hidden">
                <div className="flex items-center justify-between border-b border-white/[0.06] px-4 py-3">
                  <div className="flex items-center gap-2 font-mono text-[11px] text-white/45">
                    <LiveDot /><span>agent_run · {a.id}</span>
                  </div>
                  <span className="font-mono text-[10px] text-white/35">v2.7</span>
                </div>
                <div className="px-5 py-5 min-h-[230px] flex flex-col gap-2.5">
                  {a.terminal.map((s,i) => (
                    <div key={i} className="flex items-start gap-3">
                      <RoleChipMini role={s.role} />
                      <div className={`font-mono text-[12px] leading-[1.5] ${roleColor(s.role)}`}>{s.text}</div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="mt-4 grid grid-cols-3 gap-px overflow-hidden rounded-xl border border-white/[0.06] bg-white/[0.06]">
                {a.kpi.map(k => (
                  <div key={k.k} className="bg-[#070d24] p-4">
                    <div className="font-mono text-[9.5px] uppercase tracking-[0.18em] text-white/40">{k.k}</div>
                    <div className="mt-1 text-[20px] font-semibold tracking-tight nums grad-text">{k.v}</div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  </section>
);

export const AgentsHero = () => (
  <section className="relative overflow-hidden pt-32 pb-12 lg:pt-40 lg:pb-16">
    <div className="pointer-events-none absolute inset-0 bg-grid opacity-50" />
    <div className="pointer-events-none absolute -top-40 left-1/2 -translate-x-1/2 h-[420px] w-[1000px] rounded-full blur-[120px] opacity-40"
         style={{ background: "radial-gradient(closest-side, rgba(76,125,255,0.45), rgba(94,234,212,0.15), transparent 70%)" }} />
    <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
      <SectionLabel tone="accent">The Agent catalog</SectionLabel>
      <h1 className="mt-5 max-w-4xl text-[44px] leading-[1.02] tracking-tight sm:text-[58px] lg:text-[68px]">
        <span className="text-white">Eleven agents.</span>{" "}
        <span className="font-display italic grad-text">One operational nervous system.</span>
      </h1>
      <p className="mt-6 max-w-2xl text-[17px] leading-relaxed text-white/70">
        Every EkkBaz agent is custom-built for your data and tools, governed by your policies, and gets measurably better every week. Below: the eleven production agents we deploy most often &mdash; including our flagship <span className="text-white">WhatsApp Commerce Agent</span> that turns the channel your buyers already use into your fastest sales rep.
      </p>
      <div className="mt-10 grid grid-cols-2 gap-2 md:grid-cols-3 lg:grid-cols-6">
        {AGENTS.map(a => (
          <a key={a.id} href={`#${a.id}`} className={`group rounded-xl border p-3 ${a.id === "whatsapp" ? "border-emerald-300/30 bg-emerald-300/[0.04] hover:border-emerald-300/50" : "border-white/[0.06] bg-white/[0.025] hover:border-cyan-300/30 hover:bg-white/[0.05]"}`}>
            <div className={`font-mono text-[9.5px] uppercase tracking-[0.22em] ${a.id === "whatsapp" ? "text-emerald-300" : "text-cyan-300/70"}`}>{a.code}</div>
            <div className="mt-1 text-[13px] font-semibold text-white">{a.name}</div>
            <div className="text-[11px] text-white/45">{a.tag}</div>
          </a>
        ))}
      </div>
    </div>
  </section>
);
